/**
 * contactFlowMapping.ts - Shared utilities for mapping contacts to nodes and edges
 *
 * Purpose:
 *   Provides reusable functions for enriching automation flow nodes and edges with
 *   contact information based on API data. This ensures consistent contact flow
 *   visualization across both editable and read-only canvases.
 *
 * Usage:
 *   import { enrichNodesWithContacts, enrichEdgesWithContacts } from './contactFlowMapping';
 *
 * API-Driven Approach:
 *   - Contacts appear on nodes/edges based exclusively on API data (testContact.steps)
 *   - No frontend logic determines contact flow - it mirrors backend reality
 *   - A contact shows on a node if and only if the API includes that step.id in testContact.steps
 *   - Contact edges are determined by nextStepIndex (first incomplete step)
 *
 * Related:
 *   - ReadOnlyCanvas.tsx (original implementation)
 *   - AutomationFlowCanvas.tsx (consumer)
 *   - types/contacts.ts (FlowContact interface)
 */

import { type Node, type Edge } from '@xyflow/react';
import { type FlowContact } from '../types/contacts';

/**
 * Represents a contact with API step status information
 */
export interface ContactWithSteps {
	contact: {
		id?: string;
		firstName?: string | null;
		lastName?: string | null;
		profilePic?: string;
	};
	color: string;
	steps?: {
		step?: {
			id?: string;
		};
		isFinished: boolean;
	}[];
}

/**
 * Type guard to check if node data has a stepId property
 */
interface NodeDataWithStepId {
	stepId?: string;
	stepData?: {
		id?: string;
	};
}

/**
 * Enriches automation flow nodes with contact information based on API data.
 *
 * Story 4.1: API-Driven Contact-to-Node Mapping
 * A contact appears on a node if and only if the API includes that step.id in testContact.steps.
 * This ensures the frontend accurately reflects which steps each contact will hit according to
 * the backend automation logic.
 *
 * @param nodes - Array of automation flow nodes
 * @param contactsWithSteps - Array of contacts with their step status from API
 * @returns Array of nodes enriched with contact colors and counts
 */
export function enrichNodesWithContacts<T extends Record<string, unknown>>(
	nodes: Node<T>[],
	contactsWithSteps: ContactWithSteps[]
): Node<T>[] {
	// Build stepIdToContacts map exclusively from API step data
	const stepIdToContacts = new Map<string, ContactWithSteps[]>();

	contactsWithSteps.forEach(contactWithSteps => {
		if (contactWithSteps.steps) {
			contactWithSteps.steps.forEach(stepStatus => {
				const stepId = stepStatus.step?.id;
				if (!stepId) {
					return;
				}
				if (!stepIdToContacts.has(stepId)) {
					stepIdToContacts.set(stepId, []);
				}
				stepIdToContacts.get(stepId)!.push(contactWithSteps);
			});
		}
	});

	// Enrich nodes with contact data
	return nodes.map(node => {
		// Extract stepId from various possible locations in node data
		const nodeData = node.data as unknown as NodeDataWithStepId;
		const nodeStepId = nodeData?.stepId || nodeData?.stepData?.id || node.id;
		const contactsHittingNode = stepIdToContacts.get(String(nodeStepId)) || [];

		return {
			...node,
			data: {
				...node.data,
				contactColors: contactsHittingNode.map(c => c.color),
				contactCount: contactsHittingNode.length,
			},
		};
	});
}

/**
 * Enriches automation flow edges with contact information based on API data.
 *
 * Story 4.3: API-Driven Contact-to-Edge Mapping
 * Contacts are placed on edges based on their position in the API-provided steps array.
 * The edge is determined by finding the first incomplete step (nextStepIndex):
 * - If nextStepIndex === 0: contact is on edge from trigger to first step
 * - If nextStepIndex > 0: contact is on edge from previous step to next step
 * - If nextStepIndex === -1: all steps complete, contact is not on any edge
 *
 * @param edges - Array of automation flow edges
 * @param contactsWithSteps - Array of contacts with their step status from API
 * @returns Array of edges enriched with active contacts
 */
export function enrichEdgesWithContacts<T extends Record<string, unknown>>(
	edges: Edge<T>[],
	contactsWithSteps: ContactWithSteps[]
): Edge<T>[] {
	const edgeIdToContacts = new Map<string, ContactWithSteps[]>();

	contactsWithSteps.forEach((contactWithSteps, _index) => {
		if (!contactWithSteps.steps || contactWithSteps.steps.length === 0) {
			return;
		}

		// Find the first incomplete step (where the contact is heading)
		const nextStepIndex = contactWithSteps.steps.findIndex(s => !s.isFinished);

		if (nextStepIndex === -1) {
			// All steps completed - contact is not on any edge
			return;
		}

		const nextStep = contactWithSteps.steps[nextStepIndex];
		const nextStepId = nextStep.step?.id;
		if (!nextStepId) {
			return;
		}

		// Determine the source node
		let sourceId: string;
		if (nextStepIndex === 0) {
			// First step - contact is between trigger and first step
			sourceId = 'trigger';
		} else {
			// Contact is between previous step and current step
			const previousStep = contactWithSteps.steps[nextStepIndex - 1];
			const previousStepId = previousStep.step?.id;
			if (!previousStepId) {
				return;
			}
			sourceId = `step-${previousStepId}`;
		}

		const targetId = `step-${nextStepId}`;
		const edgeId = `${sourceId}-to-${targetId}`;

		// Add contact to this edge
		if (!edgeIdToContacts.has(edgeId)) {
			edgeIdToContacts.set(edgeId, []);
		}
		edgeIdToContacts.get(edgeId)!.push(contactWithSteps);
	});

	// Enrich edges with contact data
	const enrichedEdges = edges.map(edge => {
		const contactsOnEdge = edgeIdToContacts.get(edge.id) || [];

		// Convert ContactWithSteps to FlowContact format
		const activeContacts: FlowContact[] = contactsOnEdge.map(c => ({
			id: c.contact.id || '',
			firstName: c.contact.firstName || '',
			lastName: c.contact.lastName || '',
			profilePic: c.contact.profilePic,
			tags: [],
			keyFacts: [],
			color: c.color,
			isActive: true,
		}));

		return {
			...edge,
			data: {
				...edge.data,
				activeContacts,
			},
		};
	});

	return enrichedEdges;
}

/**
 * Enriches edges with contacts for PREVIEW/VISUALIZATION mode.
 * Unlike enrichEdgesWithContacts which shows contacts at their actual position,
 * this function distributes contacts across ALL edges they will encounter
 * based on their step list, providing a full flow visualization.
 *
 * @param edges - Array of automation flow edges
 * @param contactsWithSteps - Array of contacts with their step status from API
 * @returns Array of edges enriched with contacts that will pass through them
 */
export function enrichEdgesWithContactsPreview<T extends Record<string, unknown>>(
	edges: Edge<T>[],
	contactsWithSteps: ContactWithSteps[]
): Edge<T>[] {
	const edgeIdToContacts = new Map<string, ContactWithSteps[]>();

	contactsWithSteps.forEach((contactWithSteps, _index) => {
		if (!contactWithSteps.steps || contactWithSteps.steps.length === 0) {
			return;
		}

		// For preview mode, show contacts on ALL edges they will encounter
		// This includes the edge to each step in their flow
		contactWithSteps.steps.forEach((step, stepIndex) => {
			const stepId = step.step?.id;
			if (!stepId) {
				return;
			}
			let sourceId: string;

			if (stepIndex === 0) {
				// First step - edge from trigger
				sourceId = 'trigger';
			} else {
				// Subsequent steps - edge from previous step
				const previousStep = contactWithSteps.steps![stepIndex - 1];
				const previousStepId = previousStep.step?.id;
				if (!previousStepId) {
					return;
				}
				sourceId = `step-${previousStepId}`;
			}

			const targetId = `step-${stepId}`;
			const edgeId = `${sourceId}-to-${targetId}`;

			// Add contact to this edge
			if (!edgeIdToContacts.has(edgeId)) {
				edgeIdToContacts.set(edgeId, []);
			}
			edgeIdToContacts.get(edgeId)!.push(contactWithSteps);
		});
	});

	// Enrich edges with contact data
	const enrichedEdges = edges.map(edge => {
		const contactsOnEdge = edgeIdToContacts.get(edge.id) || [];

		// Convert ContactWithSteps to FlowContact format
		const activeContacts: FlowContact[] = contactsOnEdge.map(c => ({
			id: c.contact.id || '',
			firstName: c.contact.firstName || '',
			lastName: c.contact.lastName || '',
			profilePic: c.contact.profilePic,
			tags: [],
			keyFacts: [],
			color: c.color,
			isActive: true,
		}));

		return {
			...edge,
			data: {
				...edge.data,
				activeContacts,
			},
		};
	});

	return enrichedEdges;
}

/**
 * TODO: Story 4.4 - Switch Case Routing
 *
 * Currently, contacts appear on ALL nodes in their steps array, regardless of switch case branches.
 * Future enhancement: The backend API needs to include `selectedCaseId` in IAutomationStepStatus
 * to indicate which specific branch of a switch step the contact actually took.
 *
 * Once available, we should filter contacts to only show on nodes matching their actual branch path.
 * Example: If contact took Case A branch, only show them on Case A nodes, not Case B nodes.
 *
 * Implementation plan:
 * 1. Update IAutomationStepStatus interface to include selectedCaseId?: string
 * 2. In enrichNodesWithContacts, filter contacts based on matching case paths
 * 3. Track case ancestry for each node (which case branch it belongs to)
 * 4. Only show contact on node if contact's selectedCaseId matches node's case ancestry
 */
